import React, { useEffect, useRef, useState } from 'react';
import { useMaskito } from '@maskito/react';
import clsx from 'clsx';
import useOnclickOutside from 'react-cool-onclickoutside';
import { type FieldError, Controller } from 'react-hook-form';
import IconButton from 'components/IconButton';
import { type RootInputProps } from 'components/Input/RootInput/types';
import Label from 'components/Label';
import TooltipWrapper from 'components/TooltipWrapper';
import ValidationError from 'components/ValidationError';
import { useFormInputsRefContext } from 'contexts/inputsRefContext';
import useRandomIdFallback from 'hooks/useRandomIdFallback';
import { type FCC } from 'types';
import { isSupportedMaskitoInputType } from 'utils/maskito';
import styles from './RootInput.module.scss';

const RootInput: FCC<RootInputProps> = (props) => {
  const {
    name,
    label,
    control,
    type = 'text',
    required,
    icon,
    tooltipText,
    containerClassName,
    setError,
    onChangeResetTooltip,
    maskOptions,
    externalOnBlur,
    externalOnChange,
    externalOnFocus,
    externalOnClick,
    clearErrors,
    handleIconClick,
    clearErrorOnTooltipOpen = true,
    autoComplete,
    showError = true,
    rules,
    inputClassName,
    readOnly,
    maxLength,
    inputMode,
    placeholder,
    spellCheck,
    ...accessibilityProps
  } = props;

  const [toggleTooltip, setToggleTooltip] = useState<boolean>(false);
  const [onTarget, setOnTarget] = useState<boolean>(false);
  const [previousError, setPreviousError] = useState<string>('');

  const formInputRefs = useFormInputsRefContext();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const maskitoRef = useMaskito({ options: maskOptions });

  const validationErrorId = `${name}-validation-error`;
  const tooltipId = `${name}-tooltip`;

  useEffect(() => {
    if (inputRef.current && formInputRefs?.current && !formInputRefs.current[name]) {
      formInputRefs.current[name] = inputRef;
    }
  }, [formInputRefs, name]);

  const inputId = useRandomIdFallback();

  useEffect(() => {
    const handleTab = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        setToggleTooltip(false);
      }
    };

    document.addEventListener('keydown', handleTab);

    return () => {
      document.removeEventListener('keydown', handleTab);
    };
  }, []);

  const resetToggle = () => {
    if (tooltipText && toggleTooltip) {
      setToggleTooltip(false);

      if (previousError) {
        setError?.(name, { message: previousError });
      }
    }
  };

  const outsideClickRef = useOnclickOutside(resetToggle);

  const setToggle = (error?: string) => {
    setToggleTooltip(!toggleTooltip);

    if (!toggleTooltip && error && clearErrorOnTooltipOpen) {
      clearErrors(name);
    } else if (previousError) {
      setError?.(name, { message: previousError });
    }
  };

  const handleOnFocus = (error?: string) => {
    if (!onTarget) {
      setOnTarget(true);
    }

    if (previousError) {
      setPreviousError('');
    }

    if (error) {
      clearErrors(name);
    }
  };

  const handleOnBlur = () => {
    if (onTarget) {
      setOnTarget(false);
    }
  };

  const handleChange = (value: string) => {
    if (onChangeResetTooltip && tooltipText && toggleTooltip) {
      setToggleTooltip(false);
    }

    externalOnChange?.(value);
  };

  const handleInfoButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    error?: FieldError,
  ) => {
    event.preventDefault();

    setToggle(error?.message);
    handleIconClick?.();
  };

  return (
    <div className={clsx(styles.wrapper, containerClassName)} ref={outsideClickRef}>
      <Controller
        rules={rules}
        name={name}
        control={control}
        render={({ field: { onChange, onBlur, ref, value }, fieldState: { error } }) => {
          return (
            <TooltipWrapper content={tooltipText} toggle={toggleTooltip} id={tooltipId}>
              <div className={styles.container}>
                <input
                  type={maskOptions && !isSupportedMaskitoInputType(type) ? 'text' : type}
                  id={inputId}
                  value={value ?? ''}
                  required={required}
                  onChange={(event) => {
                    onChange(event.target.value);
                    handleChange(event.target.value);
                  }}
                  onBlur={() => {
                    onBlur();
                    handleOnBlur();
                    externalOnBlur?.();
                  }}
                  onFocus={() => {
                    handleOnFocus(error?.message);
                    externalOnFocus?.();
                  }}
                  onClick={() => {
                    externalOnClick?.();
                  }}
                  readOnly={readOnly}
                  maxLength={maxLength}
                  autoComplete={autoComplete}
                  inputMode={inputMode}
                  placeholder={!!(placeholder && onTarget) ? placeholder : ''}
                  spellCheck={spellCheck}
                  {...accessibilityProps}
                  aria-describedby={showError && !!error?.message ? validationErrorId : undefined}
                  aria-invalid={!!error}
                  className={clsx(styles.input, inputClassName, {
                    [styles.hasValue]: !!value,
                  })}
                  data-clarity-mask="true"
                  ref={(input: HTMLInputElement | null) => {
                    ref(input);
                    inputRef.current = input;
                    maskitoRef(input);
                  }}
                />
                <Label error={showError ? error?.message : undefined} htmlFor={inputId}>
                  {label}
                  {required && (
                    <span aria-hidden="true" className={styles.requiredStar}>
                      *
                    </span>
                  )}
                </Label>
                {!!icon && tooltipText ? (
                  <IconButton
                    icon={icon}
                    onClick={(event) => handleInfoButtonClick(event, error)}
                    className={styles.icon}
                    aria-label={`Info for ${label}.`}
                    aria-describedby={toggleTooltip ? tooltipId : undefined}
                    aria-expanded={toggleTooltip}
                    aria-haspopup="true"
                  />
                ) : (
                  <div className={styles.icon}>{icon}</div>
                )}

                {showError && !!error?.message && (
                  <ValidationError
                    setPreviousError={setPreviousError}
                    message={error.message}
                    id={validationErrorId}
                  />
                )}
              </div>
            </TooltipWrapper>
          );
        }}
      />
    </div>
  );
};

export default RootInput;
